import { array, concat, fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import willDestroy from '@ember/render-modifiers/modifiers/will-destroy';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import { isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import { isTesting, macroCondition } from '@embroider/macros';
import { tracked } from '@glimmer/tracking';
import { pick, queue } from '@nullvoxpopuli/ember-composable-helpers';
import { addDays, getHours, getMinutes, parse, setHours, setMinutes, subDays } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime } from 'date-fns-tz';
import type { DetailedChangeset } from 'ember-changeset/types';
import type { EncapsulatedTask } from 'ember-concurrency';
import { all, task, dropTask, restartableTask, timeout, type Task, type TaskInstance } from 'ember-concurrency';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import { pluralize } from 'ember-inflector';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
import ModalDialog from 'ember-modal-dialog/components/modal-dialog';
import PowerSelect from 'ember-power-select/components/power-select';
import set from 'ember-set-helper/helpers/set';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { and, gt, not, or } from 'ember-truth-helpers';
import DatetimePicker from 'garaje/components/datetime-picker';
import EntriesHostField from 'garaje/components/entries/host-field';
import InvitesBulkPreregistrationEmailConfirmation from 'garaje/components/invites/bulk-preregistration-email-confirmation';
import EditRecurringInvitesModal from 'garaje/components/invites/edit-recurring-invites-modal';
import GroupInviteLinkPanel from 'garaje/components/invites/group-invite-link/panel';
import RecurringInvitesContainer from 'garaje/components/invites/recurring-invites-container';
import type { RecurringOptions } from 'garaje/components/invites/select-recurring-invites';
import SendNotificationModal from 'garaje/components/invites/send-notification-modal';
import InvitesSinglePreregHostsField from 'garaje/components/invites/single-prereg/hosts-field';
import InvitesSpreadsheetGroupDiscardSpreadsheetConfirmation from 'garaje/components/invites/spreadsheet-group/discard-spreadsheet-confirmation';
import InvitesSpreadsheetGroupErrorCount from 'garaje/components/invites/spreadsheet-group/error-count';
import InvitesSpreadsheetInput from 'garaje/components/invites/spreadsheet-input';
import InvitesTableInviteList from 'garaje/components/invites/table/invite-list';
import type { PublicPaginationApi } from 'garaje/components/pagination';
import Pagination from 'garaje/components/pagination';
import TimePicker from 'garaje/components/time-picker';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import MessageBox from 'garaje/components/ui/classic/message-box';
import UiButton from 'garaje/components/ui-button';
import UiInputField from 'garaje/components/ui-input-field';
import featureEnabled from 'garaje/helpers/feature-enabled';
import formatTimezoneAbbreviation from 'garaje/helpers/format-timezone-abbreviation';
import preventDefault from 'garaje/helpers/prevent-default';
import type { PaginatedRecordArray } from 'garaje/infinity-models/v3-offset';
// Keep `Model` suffix here to avoid confusion with app `config`
import type ConfigModel from 'garaje/models/config';
import type Employee from 'garaje/models/employee';
import type Flow from 'garaje/models/flow';
import GroupInvite from 'garaje/models/group-invite';
import type GroupInviteInstanceTemplate from 'garaje/models/group-invite-instance-template';
import type GroupInviteLink from 'garaje/models/group-invite-link';
import type Invite from 'garaje/models/invite';
import type Location from 'garaje/models/location';
import type RecurringGroupInvite from 'garaje/models/recurring-group-invite';
import type SignInField from 'garaje/models/sign-in-field';
import type CurrentLocationService from 'garaje/services/current-location';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LocationFeatureFlagsService from 'garaje/services/location-feature-flags';
import type StateService from 'garaje/services/state';
import type DateLimits from 'garaje/types/date-time/date-limits';
import { DATE_FNS_YYYY_MM_DD } from 'garaje/utils/date-fns-tz-utilities';
import type { EmployeeSearcherTask } from 'garaje/utils/employees-searcher';
import employeesSearcherTask from 'garaje/utils/employees-searcher';
import { RECURRING_OPTIONS } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { getChangesetLatestRelationship } from 'garaje/utils/get-changeset-latest-relationship';
import isModel, { isChangesetModel } from 'garaje/utils/is-model';
import { PollingComponent } from 'garaje/utils/polling-component';
import zft from 'garaje/utils/zero-for-tests';
import type Handsontable from 'handsontable';
import _uniqBy from 'lodash/uniqBy';
import { filterBy, reads, sortBy } from 'macro-decorators';
import Papa from 'papaparse';
import { defer } from 'rsvp';
import type { ValueOf } from 'type-fest';

const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
const BODY_CLASSES = Object.freeze(['overflow-hidden']);

interface onLocationSelectedTaskOutput {
  config: ConfigModel;
  flows: Flow[];
  location: Location;
}

interface SaveOptions {
  whichInvites?: ValueOf<typeof RECURRING_OPTIONS>;
}

type SupportedGroupInvites = GroupInvite | RecurringGroupInvite;

interface InvitesSpreadsheetGroupSignature {
  Args: {
    changeset: DetailedChangeset<SupportedGroupInvites>;
    enableInviteSelection?: boolean;
    flows: Flow[];
    isEditInvite?: boolean;
    locationIsConnectedToProperty?: boolean;
    locations?: Location[];
    model: SupportedGroupInvites;
    onDateChanged?: (date: Date) => void;
    onFlowChanged?: (flow: Flow, changeset: DetailedChangeset<SupportedGroupInvites>) => void;
    onRecurringInviteChanged?: (recurringRule: string, changeset: DetailedChangeset<SupportedGroupInvites>) => void;
    onLocationSelectedTask?: Task<onLocationSelectedTaskOutput, [location: Location]>;
    onSave?: (date: Date | null, model?: SupportedGroupInvites) => void;
    shouldShowRecurrence?: boolean;
    inviteTemplatesToCopy?: GroupInviteInstanceTemplate[];
    handsontable?: unknown;
  };
}

function getErrorValidation(changeset: DetailedChangeset<SupportedGroupInvites>, key: string): string | undefined {
  return <string | undefined>changeset.error[key]?.validation;
}

function toUtc(date: Date | null): string | null {
  return date ? date.toUTCString() : null;
}

export default class InvitesSpreadsheetGroup extends PollingComponent<InvitesSpreadsheetGroupSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare locationFeatureFlags: LocationFeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare store: StoreService;
  @service declare state: StateService;

  paginationApi?: PublicPaginationApi;

  timeFormat = 'h:mm aaa';

  elementId = guidFor(this);

  @tracked hotInstance?: Handsontable;
  @tracked isSpreadsheetVisible: boolean = false;
  @tracked isSpreadsheetUpdating = false;
  @tracked loadingLocation: Location | null = null;
  @tracked spreadsheetData: {
    csvHeaders: string[];
    validRows: unknown[][];
    rowCount: number;
  } | null = null;

  @tracked isShowingEditRecurringGroupInvite = false;
  @tracked groupInviteLinkEnabled = false;
  @tracked groupInviteSpreadsheetEnabled = false;

  @tracked selectedInvites: Invite[] = [];
  @tracked invitesCount = 0;
  @tracked limit = 50;
  @tracked page = 1;
  @tracked sort = '';

  @reads('args.flows', []) flows!: Flow[];
  @reads('args.changeset') changeset!: DetailedChangeset<SupportedGroupInvites>;
  @reads('args.model') model!: SupportedGroupInvites;
  @reads('model.location') location!: Location;
  @reads('currentLocation.location.timezone', DEFAULT_TIMEZONE) defaultTimezone!: string;
  @reads('changeset.location.timezone', DEFAULT_TIMEZONE) timezone!: string;
  @reads('changeset.isNew') isNew!: boolean;
  @reads('spreadsheetData.validRows.length', 0) validRowCount!: number;
  @reads('spreadsheetData.filledRows.length', 0) totalRowCount!: number;
  @reads('changeset.flow.signInFieldPage.signInFields.content') signInFields?: SignInField[];
  @sortBy('signInFields', 'position') sortedSignInFields!: SignInField[];
  @filterBy('loadedInvites', 'hasDirtyAttributes') modifiedInvites!: Invite[];
  @filterBy('loadedInvites', 'isDeleted') deletedInvites!: Invite[];
  @filterBy('loadedGroupInviteLinks', 'hasDirtyAttributes') modifiedGroupInviteLinks!: GroupInviteLink[];

  @reads('args.model.inProgress') inProgress!: boolean;

  get shouldPoll(): boolean {
    if (macroCondition(isTesting())) return false;
    return this.inProgress;
  }

  get recurringInviteChangeset(): DetailedChangeset<RecurringGroupInvite> | undefined {
    const { changeset } = this;
    if (isChangesetModel(changeset, 'recurring-group-invite')) return changeset;
    return undefined;
  }

  @tracked parentRecurringGroupInvite?: RecurringGroupInvite;

  get recurringRuleChanged(): boolean {
    if (!this.parentRecurringGroupInvite?.hasDirtyAttributes) return false;
    const changes = this.parentRecurringGroupInvite?.changedAttributes();

    return Boolean(changes?.['recurringRule']?.length);
  }

  get supportedRecurringOptions(): RecurringOptions {
    const options: RecurringOptions = { ...RECURRING_OPTIONS };

    // if we modify anything related to the recurring rule, the THIS option is redundant
    if (this.recurringRuleChanged) options.THIS = undefined;

    return options;
  }

  get expectedArrivalTime(): Date | null {
    const { changeset } = this;
    if (isChangesetModel(changeset, 'recurring-group-invite')) {
      return changeset.startTime;
    } else if (isChangesetModel(changeset, 'group-invite')) {
      return changeset.expectedArrivalTime;
    }
    return null;
  }
  set expectedArrivalTime(value: Date | null) {
    const { changeset } = this;
    if (isChangesetModel(changeset, 'recurring-group-invite')) {
      changeset.startTime = value ?? null;
    } else if (isChangesetModel(changeset, 'group-invite')) {
      changeset.expectedArrivalTime = value ?? null;
    }
  }

  get canAccessMultipleHosts(): boolean {
    return this.state.features?.canAccessMultipleHosts ?? false;
  }

  get flowOptions(): Flow[] {
    return this.flows
      .filter((f) => !f.isProtect)
      .toSorted((f1, f2) => {
        return (f1.position ?? 0) - (f2.position ?? 0);
      });
  }

  get hasAccessToMultipleHostsFeature(): boolean {
    return !!this.state.features?.canAccessMultipleHosts;
  }

  get currentlySelectedLocation(): Location {
    const { args, loadingLocation, location } = this;
    const loading = loadingLocation ?? location;

    return args.onLocationSelectedTask?.isRunning ? loading : location;
  }

  get isForToday(): boolean {
    const { timezone, expectedArrivalTime } = this;

    if (!expectedArrivalTime) return true;

    const arrivalDay = formatInTimeZone(expectedArrivalTime, timezone, DATE_FNS_YYYY_MM_DD);
    const today = formatInTimeZone(new Date(), timezone, DATE_FNS_YYYY_MM_DD);

    return arrivalDay === today;
  }

  get fullDateString(): string {
    const { timezone, expectedArrivalTime } = this;

    if (!expectedArrivalTime) return '';

    return formatInTimeZone(expectedArrivalTime, timezone, "EEEE, MMM d 'at' h:mm aaa");
  }

  get minArrivalDate(): Date {
    const { timezone, defaultTimezone } = this;

    if (timezone === defaultTimezone) return new Date();

    // Adjustment for timezone (current location vs selected location)
    return toZonedTime(fromZonedTime(new Date(), defaultTimezone), timezone);
  }

  get minArrivalTime(): string {
    const { timezone, timeFormat, expectedArrivalTime, isNew, isForToday } = this;
    const now = formatInTimeZone(new Date(), timezone, timeFormat);

    if (!expectedArrivalTime) return now;

    return isNew && isForToday ? now : '';
  }

  get selectedDate(): Date {
    const { timezone, defaultTimezone, expectedArrivalTime } = this;
    const date = expectedArrivalTime || new Date();
    const locationDay = formatInTimeZone(date, timezone, DATE_FNS_YYYY_MM_DD);
    const defaultTzDay = formatInTimeZone(date, defaultTimezone, DATE_FNS_YYYY_MM_DD);

    // Quirk in date picker component + Moment.js!
    // Adjust the day if the current location and selected location
    // are in different calendar days based on their respective timezones
    if (locationDay > defaultTzDay) return subDays(date, -1);
    if (locationDay < defaultTzDay) return subDays(date, 1);

    return date;
  }

  get selectedTime(): string {
    const { timezone, timeFormat, expectedArrivalTime } = this;

    if (!expectedArrivalTime) return '';

    return formatInTimeZone(expectedArrivalTime, timezone, timeFormat);
  }

  get isSubmitDisabled(): boolean {
    const {
      changeset: { isInvalid, isPristine },
      submitTask: { isRunning },
      inProgress,
      validRowCount,
      isNew,
      modifiedInvites,
      deletedInvites,
      parentRecurringGroupInvite,
      modifiedGroupInviteLinks,
    } = this;

    if (isRunning) return true;
    if (isInvalid) return true;
    if (inProgress) return true;
    if (this.isSpreadsheetUpdating) return true;
    if (deletedInvites.length > 0) return false;
    if (modifiedInvites.length > 0) return false;
    if (parentRecurringGroupInvite?.hasDirtyAttributes) return false;
    if (this.featureFlags.isEnabled('visitors-generic-invite-link')) {
      if (modifiedGroupInviteLinks.length > 0) return false;
    }
    if (isPristine) return true;

    if (isNew && validRowCount === 0 && !this.groupInviteLinkEnabled) return true;

    return false;
  }

  get submitButtonText(): string {
    const {
      isNew,
      groupInviteLinkEnabled,
      changeset: { flow },
      submitTask: { isRunning },
    } = this;

    if (isNew) {
      const baseButtonText = flow?.inviteApprovalsEnabled ? 'Submit for approval' : 'Create group';

      if (isRunning) return 'Creating group...';
      if (groupInviteLinkEnabled) return `${baseButtonText} and generate a link`;

      return baseButtonText;
    }

    return isRunning ? 'Saving...' : 'Save';
  }

  get hostField(): SignInField | null {
    return this.signInFields?.find((field) => field.identifier === 'host') ?? null;
  }

  get emailSignInField(): SignInField | null {
    return this.signInFields?.find((field) => field.isEmail) ?? null;
  }

  get isEmailRequired(): boolean {
    if (
      !(
        this.featureFlags.isEnabled('visitors-required-email-field') ||
        this.locationFeatureFlags.isEnabled('visitors-required-email-field-by-location')
      )
    ) {
      return false;
    }

    return Boolean(this.emailSignInField?.required);
  }

  get validRowsWithEmail(): unknown[][] {
    const csvHeaders = this.spreadsheetData?.csvHeaders ?? [];
    const validRows = this.spreadsheetData?.validRows ?? [];
    const emailIdx = csvHeaders.indexOf('invitee_email');

    return validRows.filter((row: unknown[]): boolean => Boolean(row[emailIdx]));
  }

  get isFocusDisabledOnSpreadsheet(): boolean {
    if (this.closeAndDiscardSpreadsheetTask.isRunning) return true;
    if (!this.isSpreadsheetVisible) return true;

    return false;
  }

  get loadedInvites(): Invite[] {
    const { changeset, store } = this;

    if (!changeset.id) return [];

    return store.peekAll('invite').filter((i) => i.groupInviteId && i.groupInviteId === changeset.id);
  }

  get loadedGroupInviteLinks(): GroupInviteLink[] {
    const { changeset, store } = this;

    if (!changeset.id) return [];

    return store.peekAll('group-invite-link').filter((i) => i.groupInviteId && i.groupInviteId === changeset.id);
  }

  get isSelectionRestorable(): boolean {
    return this.selectedInvites.some((i: Invite): boolean => Boolean(i.isDeleted));
  }

  get isSelectionDeletable(): boolean {
    return this.selectedInvites.some((i: Invite): boolean => !i.isDeleted);
  }

  get changeSummary(): string {
    const { changeset, validRowCount, deletedInvites } = this;
    const summary = [];
    const changes = changeset?.changes ?? [];

    if (changes.find((c): boolean => c['key'] !== 'csv')) {
      summary.push('updating group details');
    }

    if (validRowCount > 0) {
      summary.push(`adding ${pluralize(validRowCount, 'visitor')}`);
    }

    if (deletedInvites.length > 0) {
      summary.push(`removing ${pluralize(deletedInvites.length, 'visitor')}`);
    }

    if (summary.length <= 1) return summary.join();
    if (summary.length === 2) return summary.join(' and ');

    const last = summary.pop();

    return `${summary.join(', ')}, and ${last}`;
  }

  get invalidRowCount(): number {
    return this.totalRowCount - this.validRowCount;
  }

  get showSpreadsheetCheckbox(): boolean {
    if (this.args.isEditInvite) return false;
    return this.featureFlags.isEnabled('visitors-generic-invite-link');
  }

  get showSpreadsheet(): boolean {
    if (!this.showSpreadsheetCheckbox) return true;

    return this.groupInviteSpreadsheetEnabled;
  }

  get recurringGroupDateLimits(): DateLimits {
    const today = new Date();
    const threeMonthsFromNow = addDays(today, 90);
    return {
      startDate: 0,
      endDate: Math.floor(threeMonthsFromNow.getTime() / 1000),
    };
  }

  get visitWorkflowEnabled(): boolean {
    const flow = getChangesetLatestRelationship(this.changeset, 'flow');
    return flow?.visitWorkflowEnabled ?? false;
  }

  get groupInviteLinkPanelState(): 'loading' | 'disabled' | 'enabled' {
    if (this.loadFlowVisitWorkflowTask.isRunning) {
      return 'loading';
    }
    if (this.visitWorkflowEnabled) {
      return 'disabled';
    }
    return 'enabled';
  }

  get recurringInvitesOnAvaEnabled(): boolean {
    return this.featureFlags.isEnabled('ava-recurring-invites-no-reeval');
  }

  get shouldShowRecurrenceContainer(): boolean {
    return (
      (this.args.shouldShowRecurrence ?? false) && (this.recurringInvitesOnAvaEnabled || !this.visitWorkflowEnabled)
    );
  }

  constructor(owner: unknown, args: InvitesSpreadsheetGroupSignature['Args']) {
    super(owner, args);
    const { model } = this;

    if (isModel(model, 'group-invite')) {
      this.parentRecurringGroupInvite = isPresent(model.belongsTo('recurringGroupInvite').id())
        ? <RecurringGroupInvite>model.belongsTo('recurringGroupInvite').value()
        : undefined;
    }
  }

  #setDate(date: Date): void {
    this.expectedArrivalTime = date;
    this.args.onDateChanged?.(date);
  }

  #disableBodyScrolling(): void {
    // Prevent scrolling of main page content
    document.body.classList.add(...BODY_CLASSES);
  }

  #enableBodyScrolling(): void {
    // Enable scrolling of main page content
    document.body.classList.remove(...BODY_CLASSES);
  }

  #deleteInvites(invites: Invite[]): void {
    invites.forEach((invite) => invite.deleteRecord());
  }

  #restoreInvites(invites: Invite[]): void {
    invites.forEach((invite) => invite.rollbackAttributes());
  }

  #resetSpreadsheet(): void {
    this.changeset.csv = '';
    this.hotInstance?.clear();
    this.spreadsheetData = null;
    this.isSpreadsheetVisible = false;
  }

  @action
  showSpreadsheetModal(): void {
    this.isSpreadsheetVisible = true;
    this.#disableBodyScrolling();
  }

  @action
  hideSpreadsheetModal(): void {
    this.#enableBodyScrolling();
    this.isSpreadsheetVisible = false;
  }

  @action
  setDate(date: Date | null): void {
    if (!date) return;
    const { timezone, defaultTimezone } = this;
    const locationDay = formatInTimeZone(date, timezone, DATE_FNS_YYYY_MM_DD);
    const defaultTzDay = formatInTimeZone(date, defaultTimezone, DATE_FNS_YYYY_MM_DD);
    let correctedDate = date;

    // Quirk in date picker component + Moment.js!
    // Adjust the day if the current location and selected location
    // are in different calendar days based on their respective timezones
    if (locationDay > defaultTzDay) correctedDate = subDays(date, 1);
    if (locationDay < defaultTzDay) correctedDate = subDays(date, -1);

    return this.#setDate(correctedDate);
  }

  @action
  didSelectTime(newTime: string): void {
    const { timezone, timeFormat, expectedArrivalTime } = this;
    const zonedDate = toZonedTime(expectedArrivalTime || new Date(), timezone);
    const newDate = parse(newTime, timeFormat, zonedDate);
    const date = fromZonedTime(newDate, timezone);

    this.#setDate(date);
  }

  @action
  updateFlow(flow: Flow): void {
    const { changeset } = this;

    changeset.flow = flow;
    void this.loadFlowVisitWorkflowTask.perform(flow);

    // Rebuild CSV with the new flow name as "Purpose of visit"
    if (this.spreadsheetData) {
      this.updateSpreadsheetData(this.spreadsheetData);
    }

    this.args.onFlowChanged?.(flow, changeset);
  }

  @action
  updateHost(_: unknown, employee: Employee | null): void {
    this.changeset.employee = employee;
  }

  @action
  updateAdditionalHosts(additionalHosts: Employee[]): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    this.changeset.additionalHosts = additionalHosts;
  }

  @action
  searchEmployees(term: string): TaskInstance<Employee[]> {
    const { currentlySelectedLocation, searchEmployeesTask } = this;
    const extraFilters = { locations: currentlySelectedLocation.id };

    return searchEmployeesTask.perform(term, extraFilters);
  }

  @action
  handleSpreadsheetUpdatingChange(isUpdating: boolean): void {
    this.isSpreadsheetUpdating = isUpdating;
  }

  @action
  updateSpreadsheetData(spreadsheetData: this['spreadsheetData']): void {
    const { csvHeaders, validRows } = spreadsheetData!;

    this.spreadsheetData = spreadsheetData;

    if (validRows.length > 0) {
      const flowName = this.changeset.flow?.name ?? '';
      const headers = ['Purpose of visit', ...csvHeaders];
      const rows = validRows.map((row) => [flowName, ...row]);
      this.changeset.csv = Papa.unparse([headers, ...rows]);
    } else {
      this.changeset.rollbackProperty('csv');
    }
  }

  @action
  prepareToSubmit(): void {
    // there is a parent recurring group. Bring up modal to confirm how to proceed
    if (this.parentRecurringGroupInvite) {
      this.isShowingEditRecurringGroupInvite = true;
    } else {
      void this.submitTask.perform();
    }
  }

  // Cleanup scrolling prevention class on <body>
  @action
  handleWillDestroy(): void {
    this.#enableBodyScrolling();
  }

  @action
  deleteInvite(invite: Invite): void {
    this.#deleteInvites([invite]);
  }

  @action
  bulkDeleteInvites(invites: Invite[]): void {
    this.#deleteInvites(invites);
    this.selectedInvites = [];
  }

  @action
  restoreInvite(invite: Invite): void {
    this.#restoreInvites([invite]);
  }

  @action
  bulkRestoreInvites(invites: Invite[]): void {
    this.#restoreInvites(invites);
    this.selectedInvites = [];
  }

  @action
  setRecurringRule(recurringRule: string): void {
    if (this.recurringInviteChangeset) {
      if (recurringRule === '' && <boolean>(<unknown>this.recurringInviteChangeset.isNew)) {
        // go back to a a group invite
        this.args.onRecurringInviteChanged?.(recurringRule, this.changeset);
      } else {
        this.recurringInviteChangeset.recurringRule = recurringRule;
      }
    } else {
      // transition changeset to a recurring group invite
      this.args.onRecurringInviteChanged?.(recurringRule, this.changeset);

      if (this.parentRecurringGroupInvite) {
        // if this group invite has a parent recurring group invite, update its rule
        this.parentRecurringGroupInvite.recurringRule = recurringRule;
      }
    }
  }

  @action
  updatePropsRecurringInvite(
    recurringGroupInvite: RecurringGroupInvite,
    excludedInvites: Invite[],
  ): RecurringGroupInvite {
    const changeset = this.args.changeset;
    const newExpectedArrivalTime = this.expectedArrivalTime;

    recurringGroupInvite.groupName = changeset.groupName;
    recurringGroupInvite.csv = changeset.csv;
    recurringGroupInvite.includeGroupNameInEmail = changeset.includeGroupNameInEmail;
    recurringGroupInvite.privateNotes = changeset.privateNotes;

    recurringGroupInvite.location = getChangesetLatestRelationship(changeset, 'location');
    recurringGroupInvite.employee = getChangesetLatestRelationship(changeset, 'employee');
    recurringGroupInvite.additionalHosts = getChangesetLatestRelationship(changeset, 'additionalHosts');
    recurringGroupInvite.excludedInvites = excludedInvites;

    if (newExpectedArrivalTime) {
      const existingStartTime = recurringGroupInvite.startTime!;
      const newHour = getHours(newExpectedArrivalTime);
      const newMinute = getMinutes(newExpectedArrivalTime);
      const newStartTime = setMinutes(setHours(existingStartTime, newHour), newMinute);

      recurringGroupInvite.startTime = newStartTime;
    }

    return recurringGroupInvite;
  }

  @action
  onSubmitRecurringInvite(whichInvites: SaveOptions['whichInvites']): TaskInstance<void> {
    return this.submitTask.perform({ whichInvites });
  }

  @action
  onGroupInviteLinkToggle(value: boolean): void {
    this.groupInviteLinkEnabled = value;
  }

  @action
  onGroupInviteSpreadsheetCheckboxChange(checked: boolean): void {
    this.groupInviteSpreadsheetEnabled = checked;
  }

  @action
  async performPoll(): Promise<void> {
    // refresh group invite
    const groupInvite = await this.store.findRecord('group-invite', this.args.changeset.id, {
      include: GroupInvite.groupInviteIncludes({
        includeRecurringGroupInvites: true,
        includeGroupInviteLinks: this.featureFlags.isEnabled('visitors-generic-invite-link'),
      }),
      reload: true,
    });

    // if group invite finished processing, load the first page of invites
    if (!groupInvite.inProgress) {
      this.flashMessages.showAndHideFlash('success', 'Recurring group invite processed');

      this.limit = 50;
      this.page = 1;
      this.paginationApi?.goToPage(1);
    }
  }

  @action
  registerApi(publicPaginationApi: PublicPaginationApi): void {
    this.paginationApi = publicPaginationApi;
  }

  @(employeesSearcherTask({
    filter: { deleted: false, excludeHidden: true },
    prefix: false,
  }).restartable())
  searchEmployeesTask!: EmployeeSearcherTask;

  loadFlowVisitWorkflowTask = restartableTask(async (flow: Flow): Promise<void> => {
    await flow.flowVisitWorkflow;
  });

  closeAndDiscardSpreadsheetTask = task({ drop: true }, async (): Promise<void> => {
    if (this.validRowCount === 0) {
      this.isSpreadsheetVisible = false;

      return;
    }

    // eslint-disable-next-line @typescript-eslint/await-thenable -- encapsulated tasks do not have great TS support
    const isConfirmed = await this.confirmDiscardSpreadsheetTask.perform();

    if (!isConfirmed) return;

    this.#resetSpreadsheet();
  });

  submitTask = task({ drop: true }, async (options: SaveOptions = {}): Promise<void> => {
    const {
      model,
      changeset,
      validRowsWithEmail,
      args: { isEditInvite, onSave },
      modifiedInvites,
      deletedInvites,
      flashMessages,
      showSpreadsheet,
      modifiedGroupInviteLinks,
    } = this;

    const { whichInvites } = options;

    await changeset.validate();

    if (changeset.isInvalid) return;

    const successMessage = 'Saved group invite';
    let detailsMessage = '';

    if (!showSpreadsheet) changeset.csv = '';

    // save recurring rule related models if:
    // * recurring rule has been modified
    // * making changes to future recurring group invites
    const isSavingRecurringGroupInvite =
      whichInvites === RECURRING_OPTIONS.THIS_AND_FOLLOWING || whichInvites === RECURRING_OPTIONS.ALL;

    if (isSavingRecurringGroupInvite && this.parentRecurringGroupInvite) {
      try {
        const savedRecurringGroupInvite = await this.saveRecurringInviteTask.perform(
          this.parentRecurringGroupInvite,
          whichInvites,
        );

        if (whichInvites === RECURRING_OPTIONS.ALL) {
          // reload virtual group invite and kick off polling if needed
          await this.performPoll();
          if (this.inProgress) void this.restartPolling();
        }

        onSave?.(this.expectedArrivalTime, savedRecurringGroupInvite);

        this.#cleanup();
        flashMessages.showAndHideFlash('success', successMessage, { details: detailsMessage });
      } catch (err) {
        flashMessages.showFlash('error', parseErrorForDisplay(err) || 'Unknown error. Saving failed.');
      }

      return;
    }

    const didAddVisitorsWithEmail = !changeset.notifyVisitor && validRowsWithEmail.length > 0;
    const didArrivalTimeChange =
      isEditInvite && changeset.changes.find((c): boolean => c['key'] === 'expectedArrivalTime');
    const didDeleteVisitorsWithEmail = isEditInvite && deletedInvites.some((i) => isPresent(i?.email));

    let confirmedNotifyUpdate: unknown = false;
    let updatePayload: unknown = undefined;
    let confirmedNotifyCancel = false;
    let cancellationPayload: unknown = undefined;

    if (didAddVisitorsWithEmail) {
      // eslint-disable-next-line @typescript-eslint/await-thenable -- encapsulated tasks do not have great TS support
      const notifyVisitor = await this.confirmPreregistrationEmailTask.perform();

      changeset.notifyVisitor = Boolean(notifyVisitor);
    }

    if (didArrivalTimeChange && isChangesetModel(changeset, 'group-invite')) {
      [confirmedNotifyUpdate, updatePayload] = await this.confirmGroupInviteUpdateNotification.perform();

      if (!confirmedNotifyUpdate) return;
    }

    if (didDeleteVisitorsWithEmail) {
      [confirmedNotifyCancel, cancellationPayload] = await this.confirmInviteDeletionTask.perform();

      if (!confirmedNotifyCancel) return;
    }

    try {
      // Save the Group Invite record as needed
      if (changeset.isDirty) await changeset.save();

      // Surface backend validation errors from CSV import — the backend silently drops
      // rows that fail validation (e.g. invites disabled at location) and returns the
      // error details in import_results. If all rows failed, show an error and abort
      // navigation so the user can correct the issue.
      if (!isEditInvite && isChangesetModel(changeset, 'group-invite')) {
        const errorRows = changeset.data.importResults?.['error-rows'] ?? [];
        const successRows = changeset.data.importResults?.['success-rows'] ?? [];
        if (errorRows.length > 0 && successRows.length === 0) {
          flashMessages.showFlash('error', 'Group invite created but no invitees could be added.', {
            details: 'Check that invitee emails are valid and invites are enabled for this location.',
          });
          return;
        } else if (errorRows.length > 0) {
          detailsMessage = `${errorRows.length} invitee(s) could not be added due to validation errors.`;
        }
      }

      // Commit any modifications, deletions among associated invites
      await all(_uniqBy([...modifiedInvites, ...deletedInvites], 'id').map((invite) => invite.save()));

      // If sending a custom message to deleted recipients, trigger notify
      if (cancellationPayload) {
        await all(
          deletedInvites
            .filter((i) => isPresent(i?.email))
            .map((i) => i.notifyInvite({ ...(cancellationPayload ?? {}) })),
        );
      }

      // If sending a custom message about arrival time change, trigger notify
      if (updatePayload && isModel(model, 'group-invite')) {
        await model.notifyGroupInvite({ ...updatePayload });
      }

      if (this.featureFlags.isEnabled('visitors-generic-invite-link')) {
        if (isEditInvite) {
          await all(modifiedGroupInviteLinks.map((link) => link.save()));
        } else if ('groupInviteLinks' in changeset.data) {
          if (this.groupInviteLinkEnabled) {
            const linkPromises: Promise<GroupInviteLink>[] = [];
            changeset.data.groupInviteLinks.forEach((link) => linkPromises.push(link.save()));
            await all(linkPromises);
            if (!detailsMessage) detailsMessage = 'Group invite created with a signup link for self-registration';
          } else {
            changeset.data.groupInviteLinks.forEach((link) => link.rollbackAttributes());
          }
        }
      }

      onSave?.(this.expectedArrivalTime, changeset.data);

      flashMessages.showAndHideFlash('success', successMessage, { details: detailsMessage });

      this.#cleanup();
    } catch (err) {
      const error = parseErrorForDisplay(err) || 'Unknown error. Saving failed.';

      flashMessages.showFlash('error', error);
    }
  });

  saveRecurringInviteTask = dropTask(
    async (existingRecurringGroupInvite: RecurringGroupInvite, whichInvites: ValueOf<typeof RECURRING_OPTIONS>) => {
      const changeset = this.args.changeset;
      let recurringGroupInvite: RecurringGroupInvite;

      const excludedInvites = this.deletedInvites;

      // Rollback temporary deleted status so the relationship gets sent up properly
      excludedInvites.forEach((invite) => invite.rollbackAttributes());

      if (existingRecurringGroupInvite?.id && whichInvites === RECURRING_OPTIONS.ALL) {
        // ALL
        recurringGroupInvite = this.updatePropsRecurringInvite(existingRecurringGroupInvite, excludedInvites);
      } else {
        // THIS AND FOLLOWING
        recurringGroupInvite = this.store.createRecord('recurring-group-invite', {
          groupName: changeset.groupName,
          csv: changeset.csv,
          includeGroupNameInEmail: changeset.includeGroupNameInEmail,
          privateNotes: changeset.privateNotes,
          location: getChangesetLatestRelationship(changeset, 'location'),
          employee: getChangesetLatestRelationship(changeset, 'employee'),
          additionalHosts: getChangesetLatestRelationship(changeset, 'additionalHosts'),
          recurringRule: existingRecurringGroupInvite.recurringRule,
          startTime: this.expectedArrivalTime,
          flow: getChangesetLatestRelationship(changeset, 'flow'),
          previousRecurringGroupInvite: existingRecurringGroupInvite,
          excludedInvites: excludedInvites,
        });
      }

      changeset.rollback();

      return recurringGroupInvite.save();
    },
  );

  resetSpreadsheetTask = task({ drop: true }, async (): Promise<void> => {
    // This task waits for submitTask to complete before proceeding
    while (this.submitTask.isRunning) await timeout(zft(100));

    this.#resetSpreadsheet();
  });

  @dropTask
  confirmPreregistrationEmailTask = <EncapsulatedTask<unknown, [], { abort: () => void; continue: () => void }>>(<
    unknown
  >{
    *perform(this: { context: InvitesSpreadsheetGroup; abort: () => void; continue: () => void }) {
      const deferred = defer();

      this.abort = () => deferred.resolve(false);
      this.continue = () => deferred.resolve(true);
      // @ts-expect-error casting encapsulated task
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return yield deferred.promise;
    },
  });

  loadInvitesTask = task(
    { restartable: true },
    async (page: number, limit: number): Promise<PaginatedRecordArray<Invite>> => {
      const { sort, model, location } = this;
      const offset = (page - 1) * limit;
      const include: string[] = [];
      const filter = { bulk_invite: model.id, location: location.id };
      const params: { [key: string]: unknown } = {
        filter,
        page: { offset, limit },
        include: include.join(),
      };

      if (sort) params['sort'] = sort;

      const invites = <PaginatedRecordArray<Invite>>await this.store.query('invite', params);

      this.invitesCount = invites.meta.total;

      return invites;
    },
  );

  @dropTask
  confirmDiscardSpreadsheetTask = <EncapsulatedTask<unknown, [], { abort: () => void; continue: () => void }>>(<
    unknown
  >{
    *perform(this: { context: InvitesSpreadsheetGroup; abort?: () => void; continue?: () => void }) {
      const deferred = defer();

      this.abort = () => deferred.resolve(false);
      this.continue = () => deferred.resolve(true);

      // @ts-expect-error casting encapsulated task
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return yield deferred.promise;
    },
  });

  @dropTask
  confirmInviteDeletionTask = <
    EncapsulatedTask<[boolean, unknown], [], { abort: () => void; continue: (payload: unknown) => void }>
  >(<unknown>{
    *perform(this: { context: InvitesSpreadsheetGroup; abort?: () => void; continue?: (payload: unknown) => void }) {
      const deferred = defer();

      this.abort = () => deferred.resolve([false]);
      this.continue = (payload?: unknown) => deferred.resolve([true, payload]);

      // @ts-expect-error casting encapsulated task
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return yield deferred.promise;
    },
  });

  @dropTask
  confirmGroupInviteUpdateNotification = <
    EncapsulatedTask<[boolean, unknown], [], { abort: () => void; continue: (payload: unknown) => void }>
  >(<unknown>{
    *perform(this: { context: InvitesSpreadsheetGroup; abort?: () => void; continue?: (payload: unknown) => void }) {
      const deferred = defer();

      this.abort = () => deferred.resolve([false]);
      this.continue = (payload?: unknown) => deferred.resolve([true, payload]);
      // @ts-expect-error casting encapsulated task
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return yield deferred.promise;
    },
  });

  #cleanup() {
    // Can't clear the spreadsheet while this task is running
    void this.resetSpreadsheetTask.perform();

    // Reset pagination and trigger fetch
    this.page = 0;
  }

  <template>
    {{#if this.submitTask.isRunning}}
      <div class="z-50 fixed inset-0 bg-black/5" data-test-submit-spinner>
        <LoadingSpinnerPulse />
      </div>
    {{/if}}
    {{#if this.inProgress}}
      <MessageBox
        @type="info"
        class="w-114 mb-4"
        @header="Group invite is processing"
        data-test-processing-group-invite
      >
        Please wait while we finish creating this and upcoming invites. Editing will be available shortly.
      </MessageBox>
    {{/if}}

    {{#unless @isEditInvite}}
      <p class="font-bold mb-0 -mt-2">
        Invite a group and manage their details together
      </p>
    {{/unless}}

    <fieldset
      class="space-y-4 w-1/2"
      data-test-selected-location-id={{this.currentlySelectedLocation.id}}
      data-test-selected-location-name={{this.currentlySelectedLocation.name}}
      data-test-selected-flow-id={{@changeset.flow.id}}
      data-test-selected-flow-name={{@changeset.flow.name}}
      data-test-selected-employee-id={{@changeset.employee.id}}
      data-test-selected-employee-name={{@changeset.employee.name}}
    >
      <div data-test-group-name>
        <UiInputField
          @required={{true}}
          @errors={{getErrorValidation @changeset "groupName"}}
          @update={{set @changeset "groupName"}}
          @value={{@changeset.groupName}}
          @label="Group name"
          @disabled={{this.inProgress}}
          data-test-group-name-input
          data-test-field="group-name"
        />
      </div>

      <div
        class="mb-[20px] {{if (getErrorValidation @changeset 'groupName') '!mt-0' '!mt-[-15px]'}}"
        data-test-group-checkbox
      >
        <label class="checkbox">
          <input
            data-test-checkbox-include-group-name
            class="z-20 mr-1"
            type="checkbox"
            disabled={{@isEditInvite}}
            checked={{this.changeset.includeGroupNameInEmail}}
            {{on "change" (pick "target.checked" (set this.changeset "includeGroupNameInEmail"))}}
          />
          Include group name in invite emails
        </label>
        <span class="relative top-[3px]">
          <img src="/assets/images/gray-info.svg" alt="" class="w-[16px] h-[16px] ml-1" role="presentation" />
          <EmberTooltip class="tt-group" @side="top" @delay={{200}}>
            This will appear as “{{if @changeset.employee @changeset.employee.name "Jane Doe"}}
            invited you to visit
            {{this.currentlySelectedLocation.nameWithCompanyName}}
            on
            {{if this.fullDateString this.fullDateString "Friday, Sep 1 at 4:00 pm"}}
            for
            {{if @changeset.groupName @changeset.groupName "[group name]"}}.”
          </EmberTooltip>
        </span>
      </div>

      {{#if (and (gt @locations.length 1) (not @isEditInvite))}}
        <div data-test-invite-location>
          <label for="invite-location">
            Location
          </label>
          <div>
            <PowerSelect
              @options={{@locations}}
              @selected={{this.currentlySelectedLocation}}
              @onChange={{queue (set this "loadingLocation") @onLocationSelectedTask.perform}}
              @searchEnabled={{true}}
              @searchField="nameWithCompanyName"
              as |location|
            >
              {{location.nameWithCompanyName}}
            </PowerSelect>
          </div>
          {{#if @onLocationSelectedTask.isRunning}}
            <img
              data-test-loading-location-spinner
              class="float-right relative"
              src="/assets/images/progress-small-gray.svg"
              alt="loading"
              style="margin-top: -45px; right: -24px; width: 17px;"
            />
          {{/if}}
        </div>
      {{/if}}

      <div class={{if @onLocationSelectedTask.isRunning "opacity-25 pointer-events-none"}}>
        {{#if (gt @flows.length 1)}}
          <div data-test-flow-select>
            <UiInputField
              data-test-flow-select
              @required={{true}}
              @errors={{getErrorValidation @changeset "flowCheck"}}
              @elementId="flow-select"
              as |Field|
            >
              <Field.label>Visitor type</Field.label>
              <PowerSelect
                @options={{this.flowOptions}}
                @selected={{@changeset.flow}}
                @disabled={{@isEditInvite}}
                @placeholder="Choose a purpose of visit"
                @id="flow-select-input"
                @tagName="div"
                @allowClear={{false}}
                @searchEnabled={{not @isEditInvite}}
                @searchField="name"
                @onChange={{this.updateFlow}}
                @required={{featureEnabled "visitors-no-default-visitor-type"}}
                as |option|
              >
                {{option.name}}
                {{#unless option.enabled}}
                  <span class="float-right text-carbon-50">Hidden on iPad</span>
                {{/unless}}
                {{#if option.isGlobalChild}}
                  <span class="global-flow-icon ml-1 ember-tooltip-target"></span>
                {{/if}}
              </PowerSelect>
            </UiInputField>
          </div>
        {{/if}}

        <div
          data-test-arrival-time
          data-test-timezone={{this.timezone}}
          data-test-arrival-time-in-utc={{toUtc this.expectedArrivalTime}}
        >
          <DatetimePicker
            @minDate={{this.minArrivalDate}}
            @value={{this.selectedDate}}
            @update={{this.setDate}}
            @disabled={{this.inProgress}}
            as |DP|
          >
            <div class="float-left box-border w-6/12 pr-2" data-test-invite-calendar-date>
              <label for="expectedArrivalTime">
                Arrival date
                <span class="requiredField"></span>
              </label>
              <DP.datePicker
                @disabled={{if this.parentRecurringGroupInvite true}}
                @ariaLabel="Expected arrival date"
                class="flex-none mr-2"
                @inputClass={{if (getErrorValidation @changeset "expectedArrivalTime") "error"}}
                data-test-dp-date
              />
              {{#each (getErrorValidation @changeset "expectedArrivalTime") as |errorKey|}}
                <div class="bottom-error pt-1">{{errorKey}}</div>
              {{/each}}
            </div>
          </DatetimePicker>

          <div class="float-left box-border w-6/12 pl-2" data-test-invite-calendar-time>
            <label id={{concat this.elementId "-time"}}>
              Arrival time
              <span data-test-invite-timezone-abbreviation>
                {{#if this.timezone}}
                  ({{formatTimezoneAbbreviation this.timezone}})
                {{/if}}
              </span>
              <span class="requiredField"></span>
            </label>
            <TimePicker
              @ariaLabelledBy={{concat this.elementId "-time"}}
              @min={{this.minArrivalTime}}
              @onSelectTime={{this.didSelectTime}}
              @time={{this.selectedTime}}
              @isDisabled={{this.inProgress}}
            />
          </div>
        </div>

        {{#if this.shouldShowRecurrenceContainer}}
          <div class="inline-block w-full" data-test-recurring-invite>
            <RecurringInvitesContainer
              @expectedArrivalTime={{this.expectedArrivalTime}}
              @minDate={{this.expectedArrivalTime}}
              @onChange={{this.setRecurringRule}}
              @selected={{or this.recurringInviteChangeset.recurringRule this.parentRecurringGroupInvite.recurringRule}}
              @timezone={{this.timezone}}
              @isEditInvite={{@isEditInvite}}
              @error={{getErrorValidation @changeset "recurringRule"}}
              @disabled={{this.inProgress}}
              @dateLimits={{this.recurringGroupDateLimits}}
              @shortcuts={{array "today" "threeMonthsFromNow"}}
              @isRecurringGroupInvite={{true}}
            />
          </div>
        {{/if}}

        {{#if this.hostField}}
          {{#let this.hostField as |host|}}
            {{#if this.canAccessMultipleHosts}}
              <div>
                <InvitesSinglePreregHostsField
                  @additionalHosts={{@changeset.additionalHosts}}
                  @allowClear={{not host.required}}
                  @errors={{getErrorValidation @changeset "employee"}}
                  @field={{host}}
                  @inviteChangeset={{@changeset}}
                  @primaryHost={{@changeset.employee}}
                  @required={{host.required}}
                  @searchEmployees={{this.searchEmployees}}
                  @updateAdditionalHosts={{this.updateAdditionalHosts}}
                  @updatePrimaryHost={{this.updateHost}}
                  @disabled={{this.inProgress}}
                />
              </div>
            {{else}}
              <div>
                <EntriesHostField
                  @allowClear={{not host.required}}
                  @changeset={{host}}
                  @errors={{getErrorValidation @changeset "employee"}}
                  @label={{if host.localized host.localized host.name}}
                  @required={{host.required}}
                  @selected={{@changeset.employee}}
                  @validated={{true}}
                  @search={{this.searchEmployees}}
                  @update={{this.updateHost}}
                  @disabled={{this.inProgress}}
                />
              </div>
            {{/if}}
          {{/let}}
        {{/if}}

        <div data-test-group-notes>
          <label for="group-private-notes">Private notes about the group</label>
          <textarea
            id="group-private-notes"
            value={{@changeset.privateNotes}}
            disabled={{this.inProgress}}
            data-test-group-private-notes
            {{on "input" (pick "target.value" (set @changeset "privateNotes"))}}
          />
        </div>
      </div>
      <div data-test-notify class="space-y-2 !mt-0">
        <div data-test-notify-checkbox>
          <label class="checkbox">
            <input
              class="z-20 mx-1"
              type="checkbox"
              checked={{this.changeset.notifyVisitor}}
              disabled={{this.inProgress}}
              {{on "change" (pick "target.checked" (set this.changeset "notifyVisitor"))}}
            />
            Send invite emails
            {{#if (gt this.validRowsWithEmail.length 0)}}
              to
              {{pluralize this.validRowsWithEmail.length "visitor"}}
            {{/if}}
          </label>
          <span class="relative top-[3px]">
            <img src="/assets/images/gray-info.svg" alt="" class="w-[16px] h-[16px] ml-1" role="presentation" />
            <EmberTooltip class="tt-pre-reg-email" @side="top" @delay={{200}}>
              Email guests a link to pre-register so they can sign in with just their name when they arrive.
            </EmberTooltip>
          </span>
        </div>
      </div>
    </fieldset>

    {{#if (and (not @isEditInvite) this.changeset.flow)}}
      <div class="space-y-4">
        {{#if this.showSpreadsheetCheckbox}}
          <h2 class="text-lg font-semibold">How do you want to invite people to this group?</h2>
        {{/if}}

        <div class="relative {{if this.showSpreadsheetCheckbox 'bg-carbon-5 p-6'}}">
          {{#if this.showSpreadsheetCheckbox}}
            <label
              class="flex items-center gap-3 cursor-pointer {{if this.groupInviteSpreadsheetEnabled 'mb-6'}}"
              data-test-group-invite-spreadsheet-checkbox
            >
              <input
                type="checkbox"
                checked={{this.groupInviteSpreadsheetEnabled}}
                {{on "change" (pick "target.checked" this.onGroupInviteSpreadsheetCheckboxChange)}}
                class="size-3 rounded-sm border border-carbon-30 cursor-pointer"
                data-test-group-invite-spreadsheet-checkbox
              />
              <span class="text-sm font-semibold text-md text-black">
                Send direct invites to specific people
              </span>
            </label>
          {{/if}}

          <div class="space-y-4">
            {{#if this.showSpreadsheet}}
              <div class="inline-block">
                You can add individual visitors or copy and paste data from Excel, Google Sheets, or Numbers directly
                into the form.
              </div>
            {{/if}}

            <div
              class="transition-all max-h-[calc(100vh-510px)] min-h-[405px] overflow-hidden
                {{if this.submitTask.isRunning 'pointer-events-none opacity-50'}}
                {{if
                  this.showSpreadsheet
                  'border-b opacity-100'
                  'absolute bottom-0 -z-10 pointer-events-none opacity-0 w-full'
                }}"
            >
              <InvitesSpreadsheetInput
                @flow={{this.changeset.flow}}
                @disabled={{or @onLocationSelectedTask.isRunning this.submitTask.isRunning this.inProgress}}
                @enablePropertyNotes={{@locationIsConnectedToProperty}}
                @isEmailRequired={{this.isEmailRequired}}
                @onChange={{this.updateSpreadsheetData}}
                @onUpdatingChange={{this.handleSpreadsheetUpdatingChange}}
                @inviteTemplatesToCopy={{@inviteTemplatesToCopy}}
              />
            </div>
          </div>
        </div>

        {{#if (featureEnabled "visitors-generic-invite-link")}}
          <GroupInviteLinkPanel
            @state={{this.groupInviteLinkPanelState}}
            @groupInvite={{this.changeset}}
            @timezone={{this.timezone}}
            @onGroupInviteLinkToggle={{this.onGroupInviteLinkToggle}}
          />
        {{/if}}
      </div>
    {{/if}}

    {{#if @isEditInvite}}
      <Pagination
        @page={{this.page}}
        @limit={{this.limit}}
        @total={{this.invitesCount}}
        @loadFn={{this.loadInvitesTask.perform}}
        @onPageChange={{fn (mut this.page)}}
        @registerApi={{this.registerApi}}
      >
        {{! Move pagination buttons and some custom content to the header }}
        <:header as |api|>
          <div class="flex justify-end items-center mb-4">
            <p class="flex items-center text-carbon-30 my-0" data-test-invites-list-selection-count>
              {{#if this.selectedInvites.length}}
                {{svgJar "person-gray" width="16px" height="16px" class="mr-2"}}
                <div class="mt-1">{{this.selectedInvites.length}} selected</div>
              {{else}}
                &nbsp;
              {{/if}}
            </p>
            <p class="flex items-center text-carbon-50 my-0 ml-8" data-test-invites-list-total-count>
              {{#if api.idle}}
                <div class="mt-1">
                  {{#if api.total}}
                    {{api.pageIndicator}}
                  {{else}}
                    &nbsp;
                  {{/if}}
                </div>
              {{else}}
                <div class="mt-1">Loading&hellip;</div>
              {{/if}}
            </p>
            <p class="flex items-center my-0 ml-8">
              <api.previousButton data-test-invites-list-previous-page />
              <api.nextButton data-test-invites-list-next-page />
            </p>
          </div>
        </:header>
        <:default as |api|>
          <InvitesTableInviteList
            class="invitesList !mt-0"
            @flow={{this.model.flow}}
            @invites={{api.rows}}
            @isLoading={{api.loading}}
            @enableSelection={{@enableInviteSelection}}
            @onSelection={{fn (mut this.selectedInvites)}}
            @selectedInvites={{this.selectedInvites}}
            @disabled={{this.inProgress}}
          >
            <:bulk-action as |list|>
              <div class="flex justify-end items-center absolute h-full top-0 right-0 bg-white px-2">
                {{#if this.isSelectionRestorable}}
                  <button
                    data-test-invite-restore-bulk
                    class="btn--smaller border border-carbon-40 rounded-md bg-white text-carbon-70 ml-4 hover:border-carbon-70 transition-all duration-200"
                    tabindex="-1"
                    title="Restore selected invites"
                    {{on "click" (fn this.bulkRestoreInvites list.selection)}}
                  >
                    Restore
                  </button>
                {{/if}}

                <button
                  data-test-invite-delete-bulk
                  class="btn--smaller border border-red-50 rounded-md bg-white text-red-50 ml-4 hover:bg-red-50 hover:text-white transition-all duration-200"
                  tabindex="-1"
                  title="Delete selected invites"
                  disabled={{or (not this.isSelectionDeletable) this.inProgress}}
                  {{on "click" (fn this.bulkDeleteInvites list.selection)}}
                >
                  Delete
                </button>
              </div>
            </:bulk-action>
            <:row-action as |row|>
              <td
                class="h-10 text-center align-middle bg-white border-b border-l border-carbon-10 sticky right-0 z-10 !pointer-events-auto
                  {{if row.isSelected 'selected'}}"
                data-test-invites-actions
              >
                {{! @glint-expect-error: ember data types cause issues here }}
                {{#if row.invite.isDeleted}}
                  <button
                    data-test-invite-restore={{row.invite.id}}
                    class="!opacity-100 btn btn--small btn--link !text-gem-50"
                    {{on "click" (fn this.restoreInvite row.invite)}}
                    tabindex="-1"
                    title={{concat "Restore invite for " row.invite.fullName}}
                  >
                    <span class="text-xs">Undo</span>
                  </button>
                {{else}}
                  <button
                    data-test-invite-delete={{row.invite.id}}
                    class="btn btn--small btn--square btn--nonHoverFill"
                    {{on "click" (fn this.deleteInvite row.invite)}}
                    tabindex="-1"
                    title={{concat "Delete invite for " row.invite.fullName}}
                    disabled={{this.inProgress}}
                  >
                    <span class="icon-trash"></span>
                    <span class="sr-only">
                      Delete invite for
                      {{row.invite.fullName}}
                    </span>
                  </button>
                {{/if}}
              </td>
            </:row-action>
          </InvitesTableInviteList>
        </:default>
        {{! Hide the default footer. Pagination UI is in the header on this screen. }}
        <:footer></:footer>
      </Pagination>

      {{#if (featureEnabled "visitors-generic-invite-link")}}
        <GroupInviteLinkPanel
          @state={{this.groupInviteLinkPanelState}}
          @groupInvite={{this.changeset}}
          @timezone={{this.timezone}}
          @onGroupInviteLinkToggle={{this.onGroupInviteLinkToggle}}
          @isEditing={{true}}
        />
      {{/if}}

      {{#if this.isSpreadsheetVisible}}
        <div
          class="fixed top-0 left-0 w-screen h-screen z-50 !m-0 bg-carbon-80/50"
          {{willDestroy this.handleWillDestroy}}
          data-test-spreadsheet-modal-overlay
        >
          &nbsp;
        </div>
      {{/if}}

      <ModalDialog
        @onClickOverlay={{this.hideSpreadsheetModal}}
        @clickOutsideToClose={{false}}
        @translucentOverlay={{true}}
        @renderInPlace={{true}}
        @targetAttachment="center"
        @containerClass="
      ember-modal-dialog--spreadsheet
      !max-w-[100vw]
      !max-h-[1080px]
      !w-11/12
      !h-5/6
      !fixed
      !top-1/2
      !left-1/2
      !-translate-x-1/2
      !-translate-y-1/2
      !m-0
      !transition-all
      {{if this.isSpreadsheetVisible 'opacity-100 scale-100' 'opacity-0 scale-0 pointer-events-none'}}
    "
      >
        <FocusTrapper
          class="p-8 flex flex-col h-full"
          aria-hidden={{not this.isSpreadsheetVisible}}
          @disable={{this.isFocusDisabledOnSpreadsheet}}
          data-test-spreadsheet-modal-dialog
        >
          <div class="flex-none">
            <h2 class="flex justify-between">
              <div>Add more visitors to the group</div>
            </h2>
            <p>
              Use this spreadsheet to enter details about visitors you want to add to the group.
            </p>
          </div>

          <div
            class="{{if this.submitTask.isRunning 'pointer-events-none opacity-50'}} border-b flex-auto"
            style="overflow:hidden;"
          >
            <InvitesSpreadsheetInput
              @flow={{this.changeset.flow}}
              @disabled={{this.submitTask.isRunning}}
              @enablePropertyNotes={{@locationIsConnectedToProperty}}
              @isEmailRequired={{this.isEmailRequired}}
              @onChange={{this.updateSpreadsheetData}}
              @onUpdatingChange={{this.handleSpreadsheetUpdatingChange}}
              @onHotInstance={{set this "hotInstance"}}
            />
          </div>

          <div class="mt-8 flex items-center justify-end flex-none">
            {{#if this.invalidRowCount}}
              <InvitesSpreadsheetGroupErrorCount @count={{this.invalidRowCount}} />
            {{/if}}

            <div class="flex-grow"></div>

            {{#if this.validRowCount}}
              <p class="text-papaya-50 text-sm my-0 mr-6" data-test-spreadsheet-add-summary>
                Adding
                {{pluralize this.validRowCount "visitor"}}
                to the group invite.
              </p>
            {{/if}}

            <button
              type="button"
              class="btn btn--outline"
              {{on "click" this.closeAndDiscardSpreadsheetTask.perform}}
              data-test-spreadsheet-modal-cancel
            >
              {{#if this.validRowCount}}
                Cancel
              {{else}}
                Close
              {{/if}}
            </button>

            {{#if this.validRowCount}}
              <button
                type="button"
                class="btn btn--brand ml-4"
                {{on "click" (fn (mut this.isSpreadsheetVisible) false)}}
                data-test-spreadsheet-modal-cta
              >
                Confirm
              </button>
            {{/if}}
          </div>
        </FocusTrapper>
      </ModalDialog>
    {{/if}}

    <div class="mt-8 flex items-center justify-end">
      {{#if @isEditInvite}}
        <UiButton
          class="btn btn--outline"
          disabled={{or this.submitTask.isRunning this.inProgress}}
          {{on "click" this.showSpreadsheetModal}}
          data-test-show-spreadsheet-modal
        >
          Add more visitors
        </UiButton>

        <div class="flex-auto">&nbsp;</div>

        {{#if this.changeSummary}}
          <p
            class="font-bold bg-papaya-10 text-papaya-50 text-sm px-4 py-1 my-0 mr-6 rounded-lg"
            data-test-group-invite-change-summary
          >
            {{capitalize this.changeSummary}}
          </p>
        {{/if}}
      {{else}}
        {{#if this.invalidRowCount}}
          <InvitesSpreadsheetGroupErrorCount @count={{this.invalidRowCount}} />
        {{/if}}
      {{/if}}

      {{#if (and @isEditInvite (not this.inProgress))}}
        <LinkTo
          @route="visitors.invites.new"
          @query={{hash type="group" reinviteGroupId=@changeset.id}}
          data-test-invite-again
          class="btn btn--outline mr-2"
        >
          Invite Again
        </LinkTo>
      {{/if}}

      <UiButton
        data-test-submit-button
        @type="submit"
        class="btn btn--brand {{if this.isSubmitDisabled 'disabled'}}"
        disabled={{this.isSubmitDisabled}}
        {{on "click" (preventDefault this.prepareToSubmit)}}
      >
        {{this.submitButtonText}}
      </UiButton>
    </div>

    <p class="text-sm !mt-16 mb-0">
      Learn more about
      <a href="https://envoy.help/en/articles/3444345-group-invites" target="_blank" rel="noopener">group invites</a>.
    </p>

    {{#if this.confirmPreregistrationEmailTask.isRunning}}
      {{#if this.confirmPreregistrationEmailTask.last}}
        <InvitesBulkPreregistrationEmailConfirmation
          @yes={{this.confirmPreregistrationEmailTask.last.continue}}
          @no={{this.confirmPreregistrationEmailTask.last.abort}}
        />
      {{/if}}
    {{/if}}

    {{#if this.confirmDiscardSpreadsheetTask.isRunning}}
      {{#if this.confirmDiscardSpreadsheetTask.last}}
        <InvitesSpreadsheetGroupDiscardSpreadsheetConfirmation
          {{! @glint-expect-error spreadsheetData will not be null here }}
          @rowCount={{this.spreadsheetData.rowCount}}
          @yes={{this.confirmDiscardSpreadsheetTask.last.continue}}
          @no={{this.confirmDiscardSpreadsheetTask.last.abort}}
        />
      {{/if}}
    {{/if}}

    {{#if this.confirmGroupInviteUpdateNotification.isRunning}}
      {{#if this.confirmGroupInviteUpdateNotification.last}}
        <SendNotificationModal
          @callback={{this.confirmGroupInviteUpdateNotification.last.continue}}
          @onClickBack={{this.confirmGroupInviteUpdateNotification.last.abort}}
          @action="update"
          @invites={{array null}}
          @backText="Cancel"
          @prompt={{concat
            "Send update emails to existing "
            (pluralize this.invitesCount "visitor" withoutCount=true)
            "?"
          }}
        />
      {{/if}}
    {{/if}}

    {{#if this.confirmInviteDeletionTask.isRunning}}
      {{#if this.confirmInviteDeletionTask.last}}
        <SendNotificationModal
          @callback={{this.confirmInviteDeletionTask.last.continue}}
          @onClickBack={{this.confirmInviteDeletionTask.last.abort}}
          @action="delete"
          @invites={{array null}}
          @backText="Cancel"
          @prompt={{concat
            "Send a cancellation email to the removed "
            (pluralize this.deletedInvites.length "visitor" withoutCount=true)
            "?"
          }}
        />
      {{/if}}
    {{/if}}

    {{#if this.isShowingEditRecurringGroupInvite}}
      <EditRecurringInvitesModal
        @onSubmit={{this.onSubmitRecurringInvite}}
        @modelName="group invite"
        @scope={{if
          this.supportedRecurringOptions.THIS
          this.supportedRecurringOptions.THIS
          this.supportedRecurringOptions.THIS_AND_FOLLOWING
        }}
        @close={{set this "isShowingEditRecurringGroupInvite" false}}
        @options={{this.supportedRecurringOptions}}
      />
    {{/if}}
  </template>
}
